import React from 'react';
import PitchCanvas from '../PitchCanvas';
import { VisualizationEvent } from '../types';
import { UseMapVisualizationReturn } from '../../../hooks/useMapVisualization';
import AttackingDirectionControls from './AttackingDirectionControls';
import VisualizationOverlays from './VisualizationOverlays';
import ColorStyleControls from '../controls/ColorStyleControls';
import { StatType } from '../../../types/database';

interface MapRendererProps {
  events: VisualizationEvent[];
  mapVisualization: UseMapVisualizationReturn;
  statTypes: StatType[];
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName?: string;
  awayTeamName?: string;
  handleClearFilters: () => void;
  isLoading?: boolean; // Optional loading state for filter changes
}

/**
 * MapRenderer - Main component for rendering pitch visualizations
 * Handles compare mode, single view, attacking direction controls, and all visualization overlays
 * Extracted from MapsTab to be reusable across components
 */
const MapRenderer: React.FC<MapRendererProps> = ({
  events,
  mapVisualization,
  statTypes,
  homeTeamId,
  awayTeamId,
  homeTeamName = 'Home Team',
  awayTeamName = 'Away Team',
  handleClearFilters,
  isLoading = false,
}) => {
  const {
    compareMode,
    normalizeToFirstHalf,
    homeTeamAttackingDirection,
    setHomeTeamAttackingDirection,
    awayTeamAttackingDirection,
    setAwayTeamAttackingDirection,
    whiteBackground,
    filters,
  } = mapVisualization;

  // Compute hasFilteredData based on actual events passed to component
  const hasFilteredData = events && events.length > 0;

  // Helper to handle direction change for both teams
  const handleHomeTeamDirectionChange = (direction: 'left' | 'right') => {
    if (normalizeToFirstHalf) {
      setHomeTeamAttackingDirection(direction);
      setAwayTeamAttackingDirection(direction === 'left' ? 'right' : 'left');
    } else {
      setHomeTeamAttackingDirection(direction);
      setAwayTeamAttackingDirection(direction === 'left' ? 'right' : 'left');
    }
  };

  const handleAwayTeamDirectionChange = (direction: 'left' | 'right') => {
    if (normalizeToFirstHalf) {
      setAwayTeamAttackingDirection(direction);
      setHomeTeamAttackingDirection(direction === 'left' ? 'right' : 'left');
    } else {
      setAwayTeamAttackingDirection(direction);
      setHomeTeamAttackingDirection(direction === 'left' ? 'right' : 'left');
    }
  };

  // Loading overlay component
  const LoadingOverlay = () => {
    if (!isLoading) return null;
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded-lg z-10 backdrop-blur-sm">
        <div className="flex flex-col items-center space-y-2">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-sm text-gray-600 font-medium">Updating map...</div>
        </div>
      </div>
    );
  };

  if (!hasFilteredData && !isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 pt-2 pb-3">
        <div className="relative">
          <PitchCanvas className="w-full" whiteBackground={whiteBackground} />
          {/* Overlay message when filters result in no data */}
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg">
            <div className="text-center p-6">
              <div className="text-gray-600 mb-2 font-medium">No events match your filters</div>
              <div className="text-sm text-gray-500 mb-4">
                Try adjusting your filters or{' '}
                <button
                  onClick={handleClearFilters}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  clear all filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (compareMode) {
    // Compare Mode: Two pitches side by side
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 pt-2 pb-3">
        <div className="flex flex-col">
          <div className="grid grid-cols-2 gap-4">
            {/* Home Team Pitch */}
            <div className="flex flex-col">
              <AttackingDirectionControls
                teamName={homeTeamName}
                attackingDirection={homeTeamAttackingDirection}
                normalizeToFirstHalf={normalizeToFirstHalf}
                onDirectionChange={handleHomeTeamDirectionChange}
              />
              <div className="relative">
                <PitchCanvas className="w-full" whiteBackground={whiteBackground} rotation={90}>
                  <VisualizationOverlays
                    events={events}
                    mapVisualization={mapVisualization}
                    teamId={homeTeamId}
                    prefix="home"
                  />
                </PitchCanvas>
                <LoadingOverlay />
              </div>
            </div>

            {/* Away Team Pitch */}
            <div className="flex flex-col">
              <AttackingDirectionControls
                teamName={awayTeamName}
                attackingDirection={awayTeamAttackingDirection}
                normalizeToFirstHalf={normalizeToFirstHalf}
                onDirectionChange={handleAwayTeamDirectionChange}
              />
              <div className="relative">
                <PitchCanvas className="w-full" whiteBackground={whiteBackground} rotation={90}>
                  <VisualizationOverlays
                    events={events}
                    mapVisualization={mapVisualization}
                    teamId={awayTeamId}
                    prefix="away"
                  />
                </PitchCanvas>
                <LoadingOverlay />
              </div>
            </div>
          </div>
          
          {/* Combined Legend for Compare Mode */}
          <ColorStyleControls
            mapVisualization={mapVisualization}
            statTypes={statTypes}
            events={events}
            homeTeamId={homeTeamId}
            awayTeamId={awayTeamId}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            compareMode={true}
          />
        </div>
      </div>
    );
  } else {
    // Single Pitch Mode (normal view)
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 pt-2 pb-3">
        <div className="flex flex-col">
          {/* Team Labels with Attacking Direction - Show based on team filter */}
          {(() => {
            const teamIds = filters.team_id || [];
            const hasHome = teamIds.includes(homeTeamId);
            const hasAway = teamIds.includes(awayTeamId);
            const showBoth = teamIds.length === 0 || (hasHome && hasAway) || teamIds.length === 2;
            
            if (showBoth) {
              // Both teams - show both labels side by side
              return (
                <div className="flex items-center justify-between mb-1">
                  {/* Home Team */}
                  <AttackingDirectionControls
                    teamName={homeTeamName}
                    attackingDirection={homeTeamAttackingDirection}
                    normalizeToFirstHalf={normalizeToFirstHalf}
                    onDirectionChange={handleHomeTeamDirectionChange}
                  />
                  
                  {/* Away Team */}
                  <AttackingDirectionControls
                    teamName={awayTeamName}
                    attackingDirection={awayTeamAttackingDirection}
                    normalizeToFirstHalf={normalizeToFirstHalf}
                    onDirectionChange={handleAwayTeamDirectionChange}
                  />
                </div>
              );
            } else if (hasHome && !hasAway) {
              // Home team only
              return (
                <AttackingDirectionControls
                  teamName={homeTeamName}
                  attackingDirection={homeTeamAttackingDirection}
                  normalizeToFirstHalf={normalizeToFirstHalf}
                  onDirectionChange={handleHomeTeamDirectionChange}
                />
              );
            } else if (hasAway && !hasHome) {
              // Away team only
              return (
                <AttackingDirectionControls
                  teamName={awayTeamName}
                  attackingDirection={awayTeamAttackingDirection}
                  normalizeToFirstHalf={normalizeToFirstHalf}
                  onDirectionChange={handleAwayTeamDirectionChange}
                />
              );
            }
            return null;
          })()}
          
          <div className="relative">
            <PitchCanvas className="w-full" whiteBackground={whiteBackground}>
              <VisualizationOverlays
                events={events}
                mapVisualization={mapVisualization}
                prefix="global"
              />
            </PitchCanvas>
            <LoadingOverlay />
          </div>
          
          {/* Legend for Single Pitch Mode */}
          <ColorStyleControls
            mapVisualization={mapVisualization}
            statTypes={statTypes}
            events={events}
            homeTeamId={homeTeamId}
            awayTeamId={awayTeamId}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            compareMode={false}
          />
        </div>
      </div>
    );
  }
};

/**
 * EventSummary - Displays summary statistics for filtered events
 */
export const EventSummary: React.FC<{ events: VisualizationEvent[] }> = ({ events }) => {
  if (!events || events.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <h3 className="text-lg font-semibold mb-4">Event Summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="text-sm text-gray-600">Total Events</div>
          <div className="text-2xl font-bold text-blue-600">{events.length}</div>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="text-sm text-gray-600">With Trajectories</div>
          <div className="text-2xl font-bold text-green-600">
            {events.filter((e) => e.position_data && e.position_data.length > 1).length}
          </div>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg">
          <div className="text-sm text-gray-600">Unique Stat Types</div>
          <div className="text-2xl font-bold text-purple-600">
            {new Set(events.map((e) => e.stat_type_id)).size}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapRenderer;

